import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Paper,
  ColorInput,
} from "@mantine/core";
import { motion } from "framer-motion";
import { Palette, Eye, Sparkles, CheckCircle2 } from "lucide-react";
import {
  IconLayoutDashboard,
  IconSparkles,
  IconUsers,
  IconStack,
} from "@tabler/icons-react";
import { IOrganizationProps } from "../types/Organization";
import { usePerformRequest } from "../lib/usePerformRequest";
import { Endpoints } from "../lib/endpoints";
import "./ThemeSelectionModal.scss";

interface Theme {
  id: string;
  name: string;
  description: string;
  preview: string;
  available: boolean;
  icon: React.ComponentType<{ size?: string | number; className?: string }>;
}

interface ThemeSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  organization: IOrganizationProps;
  onSelectTheme?: (themeId: string, color: string) => void;
}

const themes: Theme[] = [
  {
    id: "one",
    name: "Modern Classic",
    description:
      "A clean and professional design with elegant animations and modern layouts",
    preview: "Theme One",
    available: true,
    icon: IconLayoutDashboard,
  },
  {
    id: "two",
    name: "Clean",
    description:
      "A clean and professional design with elegant animations and modern layouts",
    preview: "Clean",
    available: true,
    icon: IconSparkles,
  },
  {
    id: "three",
    name: "Attendee Style",
    description:
      "The classic organization homepage design with hero section, stats, and comprehensive event listings",
    preview: "Attendee Style",
    available: true,
    icon: IconUsers,
  },
  {
    id: "four",
    name: "Glassmorphism",
    description:
      "A stunning modern theme with glassmorphism effects, gradient orbs, and smooth animations",
    preview: "Glassmorphism",
    available: true,
    icon: IconStack,
  },
  {
    id: "five",
    name: "TheDRA Style",
    description:
      "A professional, clean design matching The Digital Risk Alliance website with focused domains, testimonials, and comprehensive sections",
    preview: "TheDRA Style",
    available: true,
    icon: IconStack,
  },
];

const ThemeSelectionModal: React.FC<ThemeSelectionModalProps> = ({
  opened,
  onClose,
  organization,
  onSelectTheme,
}) => {
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
  const [selectedPrimaryColor, setSelectedPrimaryColor] =
    useState<string>("#17265a");
  const [selectedSecondaryColor, setSelectedSecondaryColor] =
    useState<string>("#FFFFFF");
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Fetch organization theme
  const themeEndpoint = useMemo(() => {
    return Endpoints.GetGetOrganizationTheme(organization.id);
  }, [organization.id]);

  const { data: themeData } = usePerformRequest<{
    results: {
      website_index: number;
      primary_color: string;
      secondary_color: string;
    };
  }>({
    method: themeEndpoint.method,
    route: themeEndpoint.route,
  });

  // Map website_index to theme ID
  const activeThemeId = useMemo(() => {
    if (!themeData) return null;
    const data = themeData?.results;
    const websiteIndex = data?.website_index;
    if (websiteIndex === 1) return "one";
    if (websiteIndex === 2) return "two";
    if (websiteIndex === 3) return "three";
    if (websiteIndex === 4) return "four";
    if (websiteIndex === 5) return "five";
    return null;
  }, [themeData]);

  // Set initial colors from theme data
  useEffect(() => {
    if (themeData) {
      const data = (themeData as any)?.data || themeData;
      if (data?.primary_color) {
        setSelectedPrimaryColor(data.primary_color);
      }
      if (data?.secondary_color) {
        setSelectedSecondaryColor(data.secondary_color);
      }
    }
  }, [themeData]);

  const handlePreview = (themeId: string) => {
    // Create a preview URL with just the organization ID and default colors
    const previewUrl = `/preview/organizations/${
      organization.id
    }/${themeId}?primaryColor=${encodeURIComponent(
      selectedPrimaryColor
    )}&secondaryColor=${encodeURIComponent(selectedSecondaryColor)}`;
    window.open(previewUrl, "_blank");
  };

  const handleSelectThemeClick = (themeId: string) => {
    setSelectedThemeId(themeId);
    setShowColorPicker(true);
  };

  const handleConfirmSelection = () => {
    if (selectedThemeId && onSelectTheme) {
      onSelectTheme(selectedThemeId, selectedPrimaryColor);
    }
    setShowColorPicker(false);
    setSelectedThemeId(null);
    onClose();
  };

  const handleCancelColorSelection = () => {
    setShowColorPicker(false);
    setSelectedThemeId(null);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Palette size={24} style={{ color: "#17265a" }} />
          <Title order={3} style={{ color: "#17265a" }}>
            Select Homepage Theme
          </Title>
        </Group>
      }
      centered
      size="90%"
      classNames={{
        content: "theme-modal-content",
        header: "theme-modal-header",
        body: "theme-modal-body",
      }}
      styles={{
        content: {
          maxWidth: "1400px",
        },
      }}
    >
      <Stack gap="xl" mt="md">
        <Text c="dimmed" size="sm">
          Choose a theme for your organization's public homepage. Preview any
          theme to see how it will look with your organization's information.
        </Text>

        <div className="themes-grid">
          {themes.map((theme, index) => (
            <motion.div
              key={theme.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
            >
              <Paper
                className={`theme-card ${
                  theme.available ? "available" : "unavailable"
                } ${activeThemeId === theme.id ? "active-theme" : ""}`}
                p="md"
                radius="md"
                withBorder
              >
                <Stack gap="sm">
                  {/* Theme Preview Area */}
                  <div className="theme-preview-area">
                    <div className="theme-preview-content">
                      <div className="theme-preview-header">
                        <div className="theme-preview-logo">
                          {organization.logo ? (
                            <img
                              src={organization.logo}
                              alt={organization.name}
                              className="preview-logo-img"
                            />
                          ) : (
                            <div className="preview-logo-placeholder">
                              {organization.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="theme-preview-text">
                          <div className="preview-org-name">
                            {organization.name}
                          </div>
                          {organization.subtitle && (
                            <div className="preview-org-subtitle">
                              {organization.subtitle}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="theme-preview-stats">
                        <div className="preview-stat">
                          <div className="preview-stat-value">
                            {(organization as any).event_count || 0}
                          </div>
                          <div className="preview-stat-label">Events</div>
                        </div>
                        <div className="preview-stat">
                          <div className="preview-stat-value">
                            {(organization as any).event_attendees_count || 0}+
                          </div>
                          <div className="preview-stat-label">Attendees</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Theme Info */}
                  <Stack gap="xs">
                    <Group
                      justify="space-between"
                      align="flex-start"
                      wrap="nowrap"
                    >
                      <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                        <div className="theme-icon-wrapper">
                          <theme.icon size={20} className="theme-icon" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Title order={5} className="theme-name">
                            {theme.name}
                          </Title>
                          <Text size="xs" c="dimmed" mt={2} lineClamp={2}>
                            {theme.description}
                          </Text>
                        </div>
                      </Group>
                      {activeThemeId === theme.id ? (
                        <div className="theme-badge theme-badge-active">
                          <CheckCircle2 size={12} />
                          <span>Active</span>
                        </div>
                      ) : theme.available ? (
                        <div className="theme-badge">
                          <Sparkles size={12} />
                          <span>Available</span>
                        </div>
                      ) : null}
                    </Group>

                    {/* Action Buttons */}
                    <Group gap="xs" mt="xs">
                      <Button
                        variant="outline"
                        leftSection={<Eye size={14} />}
                        onClick={() => handlePreview(theme.id)}
                        style={{
                          borderColor: "#17265a",
                          color: "#17265a",
                        }}
                        className="preview-button"
                        size="xs"
                        fullWidth
                      >
                        Preview
                      </Button>
                      {theme.available && (
                        <Button
                          onClick={() => handleSelectThemeClick(theme.id)}
                          style={{ backgroundColor: "#17265a" }}
                          className="select-button"
                          size="xs"
                          fullWidth
                        >
                          Select
                        </Button>
                      )}
                    </Group>
                  </Stack>
                </Stack>
              </Paper>
            </motion.div>
          ))}
        </div>

        {themes.filter((t) => !t.available).length > 0 && (
          <Text size="xs" c="dimmed" ta="center" mt="md">
            More themes coming soon!
          </Text>
        )}
      </Stack>

      {/* Color Picker Modal */}
      <Modal
        opened={showColorPicker}
        onClose={handleCancelColorSelection}
        title={
          <Group gap="sm">
            <Palette size={20} style={{ color: "#17265a" }} />
            <Title order={4} style={{ color: "#17265a" }}>
              Choose Theme Color
            </Title>
          </Group>
        }
        centered
        size="sm"
      >
        <Stack gap="lg" mt="md">
          <Text size="sm" c="dimmed">
            Select colors for your theme. You can adjust these later in the
            preview.
          </Text>
          <ColorInput
            label="Primary Color"
            value={selectedPrimaryColor}
            onChange={setSelectedPrimaryColor}
            format="hex"
            swatches={[
              "#17265a",
              "#2563eb",
              "#7c3aed",
              "#dc2626",
              "#ea580c",
              "#ca8a04",
              "#16a34a",
              "#0891b2",
              "#0284c7",
              "#9333ea",
            ]}
            size="md"
          />
          <ColorInput
            label="Secondary Color"
            value={selectedSecondaryColor}
            onChange={setSelectedSecondaryColor}
            format="hex"
            swatches={[
              "#FFFFFF",
              "#F9FAFB",
              "#F3F4F6",
              "#E5E7EB",
              "#D1D5DB",
              "#9CA3AF",
              "#6B7280",
              "#4B5563",
              "#374151",
              "#1F2937",
            ]}
            size="md"
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={handleCancelColorSelection}
              style={{
                borderColor: "#17265a",
                color: "#17265a",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSelection}
              style={{ backgroundColor: selectedPrimaryColor }}
            >
              Confirm Selection
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
};

export default ThemeSelectionModal;
